import React from 'react';
import PropTypes from 'prop-types';

import axios from 'axios';
import Cleave from 'cleave.js/react';
// Required by Cleave, but not used directly:
import CleavePhone from 'cleave.js/dist/addons/cleave-phone.us'; // eslint-disable-line no-unused-vars

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  Card,
  Dropdown,
  Modal
} from 'react-bootstrap-5';
import ReactPolling from 'react-polling';
import ReactFlagsSelect from 'react-flags-select';

import LoadingSpinner from 'components/ui/loading_spinner';
import Button from 'shared/button';

import './registration_modal.scss';

const defaultRegionCode = "US";

export default class ClickToCallRegistrationModal extends React.Component {
  static steps = {
    stepNumberSelect: 1,
    stepShowValidation: 2,
    stepComplete: 3
  };

  static propTypes = {
    currentUser: PropTypes.shape({
      clickToCallRegistrationComplete: PropTypes.bool.isRequired,
      phoneNumbers: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        number: PropTypes.string.isRequired
      })).isRequired
    }).isRequired,
    shown: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.state = {
      clickToCallLabel: "Select a Phone Number",
      currentStep: 1,
      phoneRegionCode: defaultRegionCode,
      registrationComplete: this.props.currentUser.clickToCallRegistrationComplete,
      retryRequired: false,
      selectedClickToCallNumber: null,
      useExistingPhoneNumber: true
    };
  }

  handleModalClose = () => {
    this.setState(
      {
        clickToCallLabel: "Select a Phone Number",
        currentStep: 1,
        phoneRegionCode: "US",
        useExistingPhoneNumber: true
      },
      () => {
        this.props.onClose(this.state.registrationComplete);
      }
    );
  };

  createVerification() {
    const { currentUser } = this.props;

    axios.post(
      "/click_to_call/verifications",
      {
        country_code: this.state.phoneRegionCode,
        phone_number: this.state.selectedClickToCallNumber,
        user_id: currentUser.id
      }
    ).then((response) => {
      this.setState({
        currentStep: 2,
        callSid: response.data.call_sid,
        validationCode: response.data.validation_code,
        registrationComplete: response.data.verified,
        startPolling: true
      });
    }).catch(() => {
      alert("There was a failure to register. Please try again later.");
    });
  }

  nextStep = () => {
    const steps = ClickToCallRegistrationModal.steps;

    switch (this.state.currentStep) {
    case steps.stepNumberSelect:
      this.createVerification(); // registration
      break;
    case steps.stepShowValidation:
      this.setState({
        currentStep: 3,
        startPolling: false
      });
      break;
    case steps.stepComplete:
      this.setState(
        { registrationComplete: true },
        () => {
          this.handleModalClose();
        }
      );
      break;
    }
  };

  handleNumberSelection = (selectedNumber) => {
    this.setState({
      selectedClickToCallNumber: selectedNumber,
      clickToCallLabel: `Custom: ${selectedNumber}`
    });
  };

  handlePhoneRegionChange = (phoneRegionCode) => {
    this.setState({ phoneRegionCode });
  };

  handleStatusPoll = (data) => {
    switch (data.status) {
    case "pending":
      return true; // return true to continue polling
    case "verified":
      this.nextStep();
      return false;
    case "failed":
      this.setState({ retryRequired: true });
      return false;
    }
  };

  retryVerification = () => {
    this.setState({
      currentStep: 1,
      useExistingPhoneNumber: false,
    });
  };

  render() {
    console.log('Rendering ClickToCallRegistrationModal', this.props.shown);
    return (
      <Modal centered className="c2c-registration-modal" show={this.props.shown}>
        {this.renderStep()}
      </Modal>
    );
  }

  renderNumberSelectDropdown() {
    if (this.state.useExistingPhoneNumber) {
      return (
        <>
          <strong> Select a Phone Number</strong>
          <br />
          <Dropdown id="c2c-phone-select-dropdown" name="c2c-phone-selector">
            <Dropdown.Toggle>{this.state.clickToCallLabel}</Dropdown.Toggle>
            <Dropdown.Menu>
              {
                this.props.currentUser.phoneNumbers.map((number) => {
                  return (
                    <Dropdown.Item
                      key={number.number}
                      onClick={() => this.handleNumberSelection(number.number)}
                    >
                      {number.label}: {number.number}
                    </Dropdown.Item>
                  );
                })
              }
              <Dropdown.Item key="new_number" onClick={() => this.setState({ useExistingPhoneNumber: false })}>
                Use a different number
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </>
      );
    } else {
      return (
        <>
          <strong> Your Phone Number</strong>
          <br />
          <ReactFlagsSelect
            defaultCountry={this.state.phoneRegionCode}
            searchable={true}
            showSelectedLabel={false}
            onSelect={this.handlePhoneRegionChange}
          />
          <Cleave
            className="css-phone"
            // TODO: [i18n] Extract phoneRegionCode from t.p.currentUser
            options={{ phone: true, phoneRegionCode: this.state.phoneRegionCode }}
            onChange={e => this.handleNumberSelection(e.target.value)}
          />
        </>
      );
    }
  }

  renderNumberSelect() {
    return (
      <>
        <Modal.Header>
          <Modal.Title>Verify Phone Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>
            <p>
              To begin calling from Buildout, you need to register your phone number.
              This will allow you to call your contacts using either your phone or your web browser.
            </p>
            <br />
            {this.renderNumberSelectDropdown()}
            <br />
            You will receive a call momentarily asking you to enter a verification code.
            <br />
          </span>
        </Modal.Body>
        <Modal.Footer>
          <Button.Cancel
            name="close-button"
            onClick={this.handleModalClose}
          >
            Close
          </Button.Cancel>
          <Button.Primary
            name="registration-button"
            onClick={this.nextStep}
          >
            Register
          </Button.Primary>
        </Modal.Footer>
      </>
    );
  }

  renderValidationDisplay() {
    const { currentUser } = this.props;

    return (
      <>
        <Modal.Header>
          <Modal.Title>Verify Phone Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            Currently calling <strong>{this.state.selectedClickToCallNumber}</strong>
            <br />
            <strong>
              <div>Verification Number:</div>
            </strong>
            <br />
            <div className="row">
              <div className="col-md-8 col-md-offset-2">
                <Card>
                  <Card.Body style={{ backgroundColor: "#f0f8ff" }}>
                    <h4 className="text-center">
                      <strong>{this.state.validationCode}</strong>
                    </h4>
                  </Card.Body>
                </Card>
              </div>
            </div>
            <br />
            You will receive a call momentarily asking you to enter a verification code.
            <hr />
            <ReactPolling
              interval={3000} // milliseconds (ms)
              method={'GET'}
              render={({ isPolling }) => {
                return isPolling ? <LoadingSpinner /> : null;
              }}
              retryCount={2}
              url={`/click_to_call/verifications/status?user_id=${currentUser.id}`}
              onFailure={() => {
                alert("There was a problem verifying this number. Please try again later.");
              }} // this is optional
              onSuccess={this.handleStatusPoll}
            />
            <br />
          </div>
        </Modal.Body>
        {this.renderValidationFooter()}
      </>
    );
  }

  renderValidationFooter() {
    if (this.state.retryRequired) {
      return (
        <>
          <Modal.Footer>
            <span>There was a failure to validate your number. Please try again</span> <br />
            <Button.Cancel
              name="close-button"
              onClick={this.handleModalClose}
            >
              Cancel
            </Button.Cancel>
            <Button.Primary
              name="retry-button"
              onClick={this.retryVerification}
            >
              Retry
            </Button.Primary>
          </Modal.Footer>
        </>
      );
    } else {
      return null;
    }
  }

  renderFinishedNotice() {
    return (
      <>
        <Modal.Header>
          <Modal.Title>Registration Complete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
              <h4 className="text-center">
                <FontAwesomeIcon
                  className='fa-check-circle text-success'
                  icon={faCheckCircle}
                  size='lg'
                />
                <br />
                <strong>Registration Complete!</strong>
              </h4>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="text-center">
                <strong>
                  You are now ready to start calling your contacts from Buildout.
                </strong>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button.Primary
            name="close-button"
            onClick={this.nextStep}
          >
            &lt; Back to Contacts
          </Button.Primary>
        </Modal.Footer>
      </>
    );
  }

  renderStep() {
    const steps = ClickToCallRegistrationModal.steps;

    switch (this.state.currentStep) {
    case steps.stepNumberSelect:
      return this.renderNumberSelect();
    case steps.stepShowValidation:
      return this.renderValidationDisplay();
    case steps.stepComplete:
      return this.renderFinishedNotice();
    default:
      return (
        <span>Invalid Step Defined</span>
      );
    }
  }
}
